import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowRight, Music } from 'lucide-react';
import { ForgedLine } from '@/components/ui/forged-line';
import AnimatedSection from '@/components/ui/animated-section';

export function FeaturedArtist() {
  const { data: artist } = useQuery({
    queryKey: ['featured-artist'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, slug, genre, bio, logo_url')
        .eq('featured', true)
        .not('slug', 'is', null)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (!artist) return null;

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Image with inner shadow */}
          <AnimatedSection animation="fade-right" as="div">
            <div className="aspect-square rounded-[4px] overflow-hidden bg-card relative group">
              {artist.logo_url ? (
                <>
                  <img
                    src={artist.logo_url}
                    alt={artist.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                  <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)]" />
                </>
              ) : (
                <div className="h-full w-full flex items-center justify-center">
                  <Music className="h-24 w-24 text-muted-foreground/30" />
                </div>
              )}
            </div>
          </AnimatedSection>

          {/* Text with vertical copper bar */}
          <AnimatedSection animation="fade-left" as="div" delay={100}>
            <div className="flex gap-6">
              <div className="hidden sm:flex pt-1">
                <ForgedLine variant="vertical" width="60px" />
              </div>
              <div className="space-y-4">
                <p className="text-sm font-medium text-accent uppercase tracking-[0.12em]">Featured Artist</p>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground group">
                  <span className="relative">
                    {artist.name}
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-accent transition-all duration-300 group-hover:w-full" />
                  </span>
                </h2>
                {artist.genre && (
                  <p className="text-muted-foreground transition-colors duration-300 hover:text-muted-foreground/80">{artist.genre}</p>
                )}
                {artist.bio && (
                  <p className="text-muted-foreground leading-relaxed line-clamp-4">{artist.bio}</p>
                )}
                <Button asChild>
                  <Link to={`/artists/${artist.slug}`}>
                    View Artist <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
